// src/contexts/SupabaseAuthContext.tsx
import React, { createContext, ReactNode, useEffect, useReducer, useCallback } from 'react';
import { ActionMap, AuthState, AuthUser, JWTContextType } from '../@types/auth';
import { UserManager } from '../@types/user';
import { 
  signIn, 
  signOut, 
  getSession, 
  getCurrentUser, 
  onAuthStateChange,
  getVenueUserProfile,
  resetPassword as supabaseResetPassword,
  refreshSession,
  updatePassword,
} from '../services/authService';
import { supabase } from '../services/supabase';
import { syncVenueUserWaitlist } from '../services/edgeFunctionService';
import { ILoginHelp } from './JWTContext';

enum Types {
  Initial = 'INITIALIZE',
  Login = 'LOGIN',
  Logout = 'LOGOUT',
  Register = 'REGISTER',
  Refresh = 'REFRESH',
}

type SupabaseAuthPayload = {
  [Types.Initial]: {
    isAuthenticated: boolean;
    user: AuthUser | null;
    accessToken?: string | null;
  };
  [Types.Login]: {
    user: AuthUser;
    accessToken?: string | null;
  };
  [Types.Logout]: undefined;
  [Types.Register]: {
    user: AuthUser;
    accessToken?: string | null;
  };
  [Types.Refresh]: {
    accessToken?: string | null;
  };
};

export type SupabaseAuthActions = ActionMap<SupabaseAuthPayload>[keyof ActionMap<SupabaseAuthPayload>];

const initialState: AuthState = {
  isAuthenticated: false,
  isInitialized: false,
  user: null,
  accessToken: '',
};

const SupabaseAuthReducer = (state: AuthState, action: SupabaseAuthActions): AuthState => {
  switch (action.type) {
    case Types.Initial:
      return {
        ...state,
        isInitialized: true,
        isAuthenticated: action.payload.isAuthenticated,
        user: action.payload.user,
        accessToken: action.payload.accessToken ?? state.accessToken,
      };
    case Types.Login:
      return {
        ...state,
        isAuthenticated: true,
        user: action.payload.user,
        accessToken: action.payload.accessToken ?? '',
      };
    case Types.Logout:
      return {
        ...state,
        isAuthenticated: false,
        user: null,
        accessToken: '',
      };
    case Types.Register:
      return {
        ...state,
        isAuthenticated: true,
        user: action.payload.user,
        accessToken: action.payload.accessToken ?? '',
      };
    case Types.Refresh:
      return {
        ...state,
        accessToken: action.payload.accessToken ?? '',
      };
    default:
      return state;
  }
};

const SupabaseAuthContext = createContext<JWTContextType | null>(null);

type AuthProviderProps = { children: ReactNode };

function SupabaseAuthProvider({ children }: AuthProviderProps) {
  const [state, dispatch] = useReducer(SupabaseAuthReducer, initialState);

  // Initialize session on mount
  useEffect(() => {
    let mounted = true;

    (async () => {
      try {
        const session = await getSession();
        const user = await getCurrentUser();

        if (!mounted) return;

        let profile: AuthUser | null = null;
        if (user) {
          const venueUserProfile = await getVenueUserProfile(user.id);
          if (venueUserProfile) {
            profile = {
              // id: venueUserProfile.id,
              // email: venueUserProfile.email,
              // firstname: venueUserProfile.firstname,
              // lastname: venueUserProfile.lastname,
              // role: venueUserProfile.role,
              // status: venueUserProfile.status,
              organization_id: venueUserProfile.organization_id,
              venues_ids: venueUserProfile.venues_ids || [],
              ...venueUserProfile,
            };
          }
        }

        dispatch({
          type: Types.Initial,
          payload: {
            isAuthenticated: !!session && !!user && !!profile,
            user: profile,
            accessToken: session?.access_token || null,
          },
        });
      } catch {
        if (!mounted) return;
        dispatch({
          type: Types.Initial,
          payload: {
            isAuthenticated: false,
            user: null,
            accessToken: null,
          },
        });
      }
    })();

    // Listen to auth changes
    const { data: { subscription } } = onAuthStateChange(
      async (session, user) => {
        if (user && session) {
          const venueUserProfile = await getVenueUserProfile(user.id);
          if (venueUserProfile) {
            dispatch({
              type: Types.Login,
              payload: {
                user: {
                  organization_id: venueUserProfile.organization_id,
                  venues_ids: venueUserProfile.venues_ids || [],
                  ...venueUserProfile,
                },
                accessToken: session.access_token,
              },
            });
          } else {
            // Session without venue profile → treat as logged out (prevents Permission Denied loop).
            await signOut();
            dispatch({ type: Types.Logout });
          }
        } else {
          dispatch({ type: Types.Logout });
        }
      }
    );

    return () => {
      mounted = false;
      subscription.unsubscribe();
    };
  }, []);

  // Auth actions
  const login = useCallback(async (email: string, password: string) => {
    const { session, profile } = await signIn(email, password);

    const authUser: AuthUser = {
      organization_id: profile.organization_id,
      venues_ids: profile.venues_ids || [],
      ...profile,
    };

    dispatch({
      type: Types.Login,
      payload: {
        user: authUser,
        accessToken: session?.access_token || null,
      },
    });
  }, []);

  const register = useCallback(async (
    email: string,
    password: string,
    firstName: string,
    lastName: string
  ) => {
    const normalizedEmail = email.toLowerCase();

    const { data: signUpData, error } = await supabase.auth.signUp({
      email: normalizedEmail,
      password,
      options: {
        data: {
          first_name: firstName,
          last_name: lastName,
        },
        emailRedirectTo: `${window.location.origin}/auth/venueusers/verify-email`,
      },
    });

    if (error) throw error;

    const authUserId = signUpData.user?.id;
    if (!authUserId) {
      throw new Error('Registration failed');
    }

    const { error: profileError } = await supabase.rpc('create_venue_user_with_profile', {
      p_auth_user_id: authUserId,
      p_firstname: firstName,
      p_lastname: lastName,
      p_email: normalizedEmail,
    });

    if (profileError) {
      throw new Error(profileError.message);
    }

    if (signUpData.session) {
      const venueUserProfile = await getVenueUserProfile(authUserId);
      if (venueUserProfile) {
        dispatch({
          type: Types.Register,
          payload: {
            user: {
              organization_id: venueUserProfile.organization_id,
              venues_ids: venueUserProfile.venues_ids || [],
              ...venueUserProfile,
            },
            accessToken: signUpData.session.access_token,
          },
        });
      }
    }
  }, []);

  const logout = useCallback(async () => {
    await signOut();
    dispatch({ type: Types.Logout });
  }, []);

  const refresh = useCallback(async (): Promise<void> => {
    try {
      // Refresh the session to get a new access token
      const session = await refreshSession();
      if (session) {
        // Also refresh the venue user profile to get latest data
        const user = await getCurrentUser();
        let profile: AuthUser | null = null;
        
        if (user) {
          const venueUserProfile = await getVenueUserProfile(user.id);
          if (venueUserProfile) {
            profile = {
              organization_id: venueUserProfile.organization_id,
              venues_ids: venueUserProfile.venues_ids || [],
              ...venueUserProfile,
            };
          }
        }

        dispatch({ 
          type: Types.Refresh, 
          payload: { accessToken: session.access_token } 
        });

        // Update user if profile was fetched
        if (profile) {
          dispatch({
            type: Types.Login,
            payload: {
              user: profile,
              accessToken: session.access_token,
            },
          });
        }
      } else {
        // If no session, logout
        dispatch({ type: Types.Logout });
      }
    } catch (error) {
      console.error('Error refreshing session:', error);
      // If refresh fails, the session might be expired - logout
      dispatch({ type: Types.Logout });
      throw error;
    }
  }, []);

  const recover = useCallback(async (data: UserManager) => {
    // Password recovery - send reset email
    await supabaseResetPassword(data.email);
    return {
      id: '',
      verifyEmailToken: undefined,
      resetToken: undefined,
      code: undefined,
    } as ILoginHelp;
  }, []);

  const resetPassword = useCallback(async (data: ILoginHelp & { newpassword?: string }) => {
    // Password reset - update password using Supabase
    if (!data.newpassword) {
      throw new Error('New password is required');
    }
    
    await updatePassword(data.newpassword);
    
    return {
      id: data.id || '',
      verifyEmailToken: data.verifyEmailToken,
      resetToken: data.resetToken,
      code: data.code,
    } as ILoginHelp;
  }, []);

  const signUp = useCallback(async (
    email: string,
    phoneNumber: string,
    firstName: string,
    lastName: string,
    venue: string,
    venueType: string[]
  ) => {
    await syncVenueUserWaitlist({
      email: email.toLowerCase(),
      phoneNumber,
      firstName,
      lastName,
      venue,
      venueType,
    });
  }, []);

  const validateCode = useCallback(async (data: ILoginHelp) => {
    // Code validation - this would need to be handled by Supabase
    return data;
  }, []);

  const value: JWTContextType = {
    ...state,
    method: 'jwt',
    login,
    register,
    logout,
    refresh,
    recover,
    resetPassword,
    signUp,
    validateCode,
  };

  return (
    <SupabaseAuthContext.Provider value={value}>
      {children}
    </SupabaseAuthContext.Provider>
  );
}

export { SupabaseAuthContext, SupabaseAuthProvider };

